/**
 * Hooks da tela /catalog/suppliers/$supplierId/arts (TH-55167).
 *
 * Backend (TH-55164):
 *   GET /catalog/suppliers/{supplier_id}/arts?status&active&page&per_page
 *
 * Status válidos: draft | pending_review | active | rejected | archived.
 *
 * Acesso: super_admin + catalog_operator. RLS no backend usa
 * `SET LOCAL ROLE priceshow_admin` para BYPASS — o vínculo
 * catalog_operator→supplier é enforçado por path param + audit, não por
 * RLS de coluna. (Frontend só consome.)
 *
 * Schema de resposta usa SupplierArtRead, que difere de CustomArtRead em:
 *   - sem origin_label/supplier_name
 *   - tem `tags: string[]`, `visibility`, `status` sempre presentes
 *   - `supplier_id` obrigatório
 */

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

export type SupplierArtStatus =
  | "draft"
  | "pending_review"
  | "active"
  | "rejected"
  | "archived";

export interface SupplierArtRead {
  id: string;
  tenant_id: string | null;
  supplier_id: string | null;
  store_id: string | null;
  area_id: string | null;
  uploaded_by: string;
  name: string;
  art_url: string;
  width: number;
  height: number;
  file_size_bytes: number;
  mime_type: string;
  active: boolean;
  scope: string;
  status: SupplierArtStatus;
  visibility: string;
  tags: string[];
  valid_from: string | null;
  valid_until: string | null;
  created_at: string;
  updated_at: string;
}

export interface SupplierArtListMeta {
  page: number;
  per_page: number;
  total: number;
}

export interface SupplierArtListResponse {
  data: SupplierArtRead[];
  meta: SupplierArtListMeta;
}

export interface SupplierArtsFilters {
  supplierId: string;
  status?: SupplierArtStatus | "all";
  active?: boolean;
  page?: number;
  per_page?: number;
}

function buildQuery(filters: SupplierArtsFilters): string {
  const sp = new URLSearchParams();
  if (filters.status && filters.status !== "all") {
    sp.set("status", filters.status);
  }
  if (filters.active !== undefined) sp.set("active", String(filters.active));
  sp.set("page", String(filters.page ?? 1));
  sp.set("per_page", String(filters.per_page ?? 20));
  return sp.toString();
}

async function fetchSupplierArts(
  filters: SupplierArtsFilters,
): Promise<SupplierArtListResponse> {
  const res = await fetch(
    `/api/catalog/suppliers/${filters.supplierId}/arts?${buildQuery(filters)}`,
    { credentials: "include" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { detail?: string };
    throw new Error(body.detail ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as SupplierArtListResponse;
}

export function useSupplierArts(
  filters: SupplierArtsFilters,
): UseQueryResult<SupplierArtListResponse, Error> {
  return useQuery({
    queryKey: ["supplier-arts", filters],
    queryFn: () => fetchSupplierArts(filters),
    enabled: !!filters.supplierId,
    staleTime: 30_000,
  });
}
